import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthController } from '@/features/auth/controller/auth.controller';
import { useActiveSeason } from '@/hooks/useActiveSeason';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { offlineQueryCache } from '@/lib/offlineQueryCache';
import { teamListQuery } from './useTeams';

/**
 * A coach's teams and rosters, loaded once signed in while the network is
 * there: the home page does not read them, so a coach who only opened it
 * would find no team offline in the gym (VOL-14). Off with the offline cache.
 */
export function useTeamListWarmup(): void {
  const client = useQueryClient();
  const isCoach = useAuthController((s) => s.user?.role === 'COACH');
  const season = useActiveSeason();
  const online = useOnlineStatus();

  useEffect(() => {
    if (!offlineQueryCache || !isCoach || !online) return;
    // Fresh enough in the cache (another screen, or this one a moment ago): no request.
    void client.prefetchQuery(teamListQuery(season)).catch(() => {});
  }, [client, isCoach, season, online]);
}
